import { useContext, useState, useRef } from "react";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { saveLocador } from "../../../../application/locadores";
import { saveLocatario } from "../../../../application/locatarios";
import { saveContrato } from "../../../../application/contratos";
import { FaFileUpload, FaCheck, FaTimes, FaSpinner } from "react-icons/fa";

// Convert Excel serial date to YYYY-MM-DD
const excelDateToISO = (serial) => {
  if (!serial || typeof serial !== "number" || serial < 1000) return "";
  const date = new Date((serial - 25569) * 86400000);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

// Separate a raw Excel cell into name + up to 2 phones + observaciones.
// Examples:
//   "Luciana  2215730477 scaturice"       -> { nombre: "Luciana", telefono: "2215730477", telefono2: "", observaciones: "scaturice" }
//   "Miguez 556039 CLAUDIA 566965"        -> { nombre: "Miguez", telefono: "556039", telefono2: "566965", observaciones: "CLAUDIA" }
//   "Cavia  Maria   Tel: 15-577473"       -> { nombre: "Cavia Maria", telefono: "15-577473", telefono2: "", observaciones: "" }
//   "Antonow Guillermo 1133915000 Silvina" -> { nombre: "Antonow Guillermo", telefono: "1133915000", telefono2: "", observaciones: "Silvina" }
const parseNombreTelefono = (raw) => {
  const empty = { nombre: "", telefono: "", telefono2: "", observaciones: "" };
  if (!raw || typeof raw !== "string") return empty;
  // Strip "Tel:" / "te " / "tel " labels and collapse whitespace.
  let str = raw.replace(/\b(tel|te)\s*:?\s+/gi, " ").replace(/\s+/g, " ").trim();
  if (!str) return empty;

  // A phone "run" is 5+ consecutive digits (with optional separators). We split the
  // string into alternating text / phone segments and assign them below.
  const phoneRe = /(\d[\d\s\-*/.()]{3,}\d|\d{4,})/g;
  const segs = [];
  let cursor = 0;
  let m;
  while ((m = phoneRe.exec(str)) !== null) {
    if (m.index > cursor) segs.push({ type: "text", text: str.slice(cursor, m.index).trim() });
    segs.push({ type: "phone", text: m[0].trim() });
    cursor = m.index + m[0].length;
  }
  if (cursor < str.length) segs.push({ type: "text", text: str.slice(cursor).trim() });

  const phones = segs.filter((s) => s.type === "phone").map((s) => s.text);
  const texts = segs.filter((s) => s.type === "text" && s.text).map((s) => s.text);

  // "423950 578183" was picked up as a single phone run because of the internal space.
  // If we only got one phone and it contains a space, split it into two.
  if (phones[0] && !phones[1] && /\s/.test(phones[0])) {
    const parts = phones[0].split(/\s+/).filter((p) => p.replace(/\D/g, "").length >= 4);
    if (parts.length >= 2) {
      phones[0] = parts[0];
      phones[1] = parts[1];
    }
  }

  // Pick the first text with actual letters as the name; the rest become observaciones.
  const hasLetters = (s) => /[a-zA-ZñÑáéíóúÁÉÍÓÚ]/.test(s);
  let nombre = "";
  const otherTexts = [];
  for (const t of texts) {
    if (!nombre && hasLetters(t)) nombre = t;
    else otherTexts.push(t);
  }
  if (!nombre) nombre = str; // no letters found anywhere — leave raw string as name

  return {
    nombre,
    telefono: phones[0] || "",
    telefono2: phones[1] || "",
    observaciones: otherTexts.join(" ").trim(),
  };
};

// Parse AUMENTO field: "1/3/26 index cuat" -> { tipo, frecuencia, proximoAumento }
const parseAumento = (raw) => {
  if (!raw || typeof raw !== "string") return { tipo: "IPC", frecuencia: "TRIMESTRAL", proximoAumento: "" };
  const str = raw.trim().toLowerCase();

  // Extract type
  let tipo = "IPC";
  if (/index|indx|idx/i.test(str)) tipo = "INDEX";
  else if (/ipc/i.test(str)) tipo = "IPC";
  else if (/fijo/i.test(str)) tipo = "FIJO";

  // Extract frequency
  let frecuencia = "TRIMESTRAL";
  if (/sem/i.test(str)) frecuencia = "SEMESTRAL";
  else if (/cuat|x\s*4|x4/i.test(str)) frecuencia = "CUATRIMESTRAL";
  else if (/trim|x\s*3|x3/i.test(str)) frecuencia = "TRIMESTRAL";
  else if (/anu/i.test(str)) frecuencia = "ANUAL";

  // Extract date - try patterns like "1/3/26", "01/4/26", "1/7/26"
  let proximoAumento = "";
  const dateMatch = str.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (dateMatch) {
    let day = parseInt(dateMatch[1]);
    let month = parseInt(dateMatch[2]);
    let year = parseInt(dateMatch[3]);
    if (year < 100) year += 2000;
    // Validate and fix
    if (month > 12) { const tmp = day; day = month; month = tmp; }
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      proximoAumento = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }

  return { tipo, frecuencia, proximoAumento };
};

// Borderless-looking input for the editable preview tables.
// Shows a subtle ring on hover/focus so users can tell cells are editable.
const CELL_INPUT_CLS =
  "w-full bg-transparent border border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary rounded px-1.5 py-1 text-xs text-gray-800 dark:text-gray-100";

function ImportContratos({ embedded = false }) {
  const { refreshAll } = useContext(AlquileresContext);
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null); // { locadores, locatarios, contratos, raw }
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [workbookRef, setWorkbookRef] = useState(null);
  const [xlsxRef, setXlsxRef] = useState(null);
  const [locadorOverride, setLocadorOverride] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState("");

  const processFile = async (file) => {
    if (!file) return;
    const isExcel = /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!isExcel) {
      setResult({ success: false, message: `Archivo no soportado: ${file.name}. Usá .xlsx, .xls o .csv` });
      return;
    }
    setResult(null);
    setPreview(null);
    setLocadorOverride("");
    setFileName(file.name);
    try {
      const XLSX = await import("xlsx");
      setXlsxRef(XLSX);
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      setWorkbookRef(workbook);
      setSheetNames(workbook.SheetNames);
      // Auto-select first sheet with data
      const firstWithData = workbook.SheetNames.find((name) => {
        const sheet = workbook.Sheets[name];
        const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        return raw.length > 3;
      });
      if (firstWithData) {
        setSelectedSheet(firstWithData);
        processSheet(workbook, XLSX, firstWithData);
      }
    } catch (error) {
      console.error("Error al leer archivo:", error);
      setResult({ success: false, message: "Error al leer el archivo." });
    }
  };

  const handleFile = (e) => processFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const processSheet = (workbook, XLSX, sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

    // Header aliases: maps logical field -> possible column header strings (case-insensitive)
    const HEADER_ALIASES = {
      locador: ["locador"],
      locatario: ["locatario", "inquilino", "loacatario"],
      propiedad: ["propiedad", "direccion", "dirección"],
      monto: ["monto", "alquiler", "importe", "precio"],
      porcentaje: ["%", "porcentaje"],
      adm: ["adm"],
      total: ["total"],
      vence: ["vence", "vencimiento"],
      aumento: ["aumento"],
      fechaInicio: ["mes inicio", "fecha inicio"],
      valorInicial: ["valor inicio", "valor inicial", "monto inicial"],
      ipc: ["ipc"],
      icl: ["icl"],
      casaPropia: ["casa prop", "casa propia", "cp", "cprop", "cpropia"],
    };

    const matchHeader = (cell, aliases) => {
      if (typeof cell !== "string") return false;
      const lower = cell.trim().toLowerCase();
      if (!lower) return false;
      return aliases.some((a) => lower === a || lower.startsWith(a) || lower.endsWith(a));
    };

    // STOP_KEYWORDS / headerKeysIn / isHeaderWord are reused across header detection
    // and the data-row loop below.
    const STOP_KEYWORDS = /^\s*(gastos?|gatos?|expensas?|enero|febrero|marzo|abril|mayo|junio|julio|agosto|setiembre|septiembre|octubre|noviembre|diciembre)\b/i;
    const headerKeysIn = (row) => {
      const matched = new Set();
      row.forEach((cell) => {
        if (typeof cell === "number" && cell > 0 && cell < 1) {
          matched.add("porcentaje");
          return;
        }
        for (const [key, aliases] of Object.entries(HEADER_ALIASES)) {
          if (matchHeader(cell, aliases)) matched.add(key);
        }
      });
      return matched;
    };
    const isHeaderWord = (str) => {
      if (!str) return false;
      for (const aliases of Object.values(HEADER_ALIASES)) {
        if (matchHeader(str, aliases)) return true;
      }
      return false;
    };

    // Find header row: prefer rows that have LOCATARIO/INQUILINO.
    let headerIdx = -1;
    for (let i = 0; i < Math.min(15, raw.length); i++) {
      if (raw[i].some((c) => matchHeader(c, HEADER_ALIASES.locatario))) {
        headerIdx = i;
        break;
      }
    }
    // Fallback: a header row that has VENCE/VENCIMIENTO + FECHA INICIO
    // (some sheets like RODRIGUEZ omit the LOCATARIO label and put the locador name in that cell).
    let usedFallbackHeader = false;
    if (headerIdx === -1) {
      for (let i = 0; i < Math.min(15, raw.length); i++) {
        const keys = headerKeysIn(raw[i]);
        if (keys.has("vence") && keys.has("fechaInicio")) {
          headerIdx = i;
          usedFallbackHeader = true;
          break;
        }
      }
    }
    if (headerIdx === -1) {
      setResult({ success: false, message: "No se encontro fila de headers (LOCATARIO/INQUILINO o VENCE+FECHA INICIO)." });
      return;
    }

    // Map header field -> column index
    const cols = {};
    raw[headerIdx].forEach((cell, idx) => {
      // Some sheets use a numeric value (e.g., 0.05) instead of the "%" string as header
      if (cols.porcentaje === undefined && typeof cell === "number" && cell > 0 && cell < 1) {
        cols.porcentaje = idx;
        return;
      }
      for (const [key, aliases] of Object.entries(HEADER_ALIASES)) {
        if (cols[key] === undefined && matchHeader(cell, aliases)) {
          cols[key] = idx;
        }
      }
    });

    // When the LOCATARIO label was missing, fall back to the conventional column layout:
    // 0:locatario, 1:propiedad, 2:monto, 3:%, 4:adm, 5:total, 7:aumento.
    if (usedFallbackHeader) {
      if (cols.locatario === undefined) cols.locatario = 0;
      if (cols.propiedad === undefined) cols.propiedad = 1;
      if (cols.monto === undefined) cols.monto = 2;
      if (cols.porcentaje === undefined) cols.porcentaje = 3;
      if (cols.adm === undefined) cols.adm = 4;
      if (cols.total === undefined) cols.total = 5;
      if (cols.aumento === undefined) cols.aumento = 7;
    }

    // Format A (master): LOCADOR is a column. Format B (per-owner): no LOCADOR col,
    // locador comes from a title row above headers and from section-divider rows in body.
    const isPerOwnerFormat = cols.locador === undefined;

    // Find the first plausible locador name in a row of cells (skip months, header words,
    // digit-only cells, short strings).
    const monthRe = /^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)$/i;
    const findLocadorInRow = (row) => {
      for (const cell of row) {
        if (typeof cell !== "string") continue;
        const t = cell.trim();
        if (!t || monthRe.test(t) || t.length < 3) continue;
        const letters = (t.match(/[a-zA-ZñÑáéíóúÁÉÍÓÚ]/g) || []).length;
        if (letters < 3) continue;
        if (isHeaderWord(t)) continue;
        return t;
      }
      return "";
    };

    let currentLocador = "";
    if (isPerOwnerFormat) {
      // First scan rows above the header
      for (let i = 0; i < headerIdx; i++) {
        currentLocador = findLocadorInRow(raw[i]);
        if (currentLocador) break;
      }
      // If still nothing (e.g. RODRIGUEZ-style: locador name lives in the header row itself), scan it
      if (!currentLocador) currentLocador = findLocadorInRow(raw[headerIdx]);
    }

    // Peso amounts are stored as whole integers (no cents).
    // The % is the only field that keeps 2 decimals (e.g. 4.5%).
    const roundInt = (n) => Math.round(Number(n) || 0);
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

    const dataRows = raw.slice(headerIdx + 1);
    const locadoresMap = new Map(); // nombre -> { nombre, telefono }
    const locatariosMap = new Map();
    const contratos = [];
    let stopProcessing = false;

    dataRows.forEach((row) => {
      if (stopProcessing) return;

      const c0 = String(row[0] || "").trim();

      // Skip TOTAL / SUBTOTAL summary rows without stopping (more sections may follow)
      if (/^(total|subtotal)\b/i.test(c0)) return;

      // Detect a header-like row inside the data area. Three sub-cases:
      //  (a) full new contract header   -> re-map columns + update locador
      //  (b) metadata-only divider      -> only update locador, keep existing cols
      //  (c) payment/cobros table       -> stop processing
      const matchedKeys = headerKeysIn(row);
      if (matchedKeys.size >= 3) {
        const hasPrimary = matchedKeys.has("monto") || matchedKeys.has("propiedad");
        const hasContractDates =
          matchedKeys.has("vence") || matchedKeys.has("aumento") || matchedKeys.has("fechaInicio");
        const isFullContractHeader = hasPrimary && hasContractDates;
        // Only metadata trailers (MES INICIO / VALOR INICIO / IPC / ICL) -> divider
        const isMetadataOnlyDivider =
          !matchedKeys.has("monto") &&
          !matchedKeys.has("propiedad") &&
          !matchedKeys.has("total") &&
          !matchedKeys.has("locatario");

        if (isFullContractHeader) {
          // Another contract block (e.g. HOTZ section after LOREDO). Re-map columns
          // and take the new locador from col 0 if it's not itself a header word.
          const newCols = {};
          row.forEach((cell, idx) => {
            if (newCols.porcentaje === undefined && typeof cell === "number" && cell > 0 && cell < 1) {
              newCols.porcentaje = idx;
              return;
            }
            for (const [key, aliases] of Object.entries(HEADER_ALIASES)) {
              if (newCols[key] === undefined && matchHeader(cell, aliases)) {
                newCols[key] = idx;
              }
            }
          });
          // In per-owner blocks col 0 holds the locador name on the header row and the
          // tenant name on data rows below. If the new header doesn't map locatario, default to col 0.
          if (newCols.locatario === undefined && c0 && !isHeaderWord(c0)) {
            newCols.locatario = 0;
          }
          // Reset cols to the new mapping (don't merge: column positions can shift)
          Object.keys(cols).forEach((k) => delete cols[k]);
          Object.assign(cols, newCols);
          // Only update the locador if c0 looks like a real owner name (>=4 letters).
          // Avoids treating literal column-label cells like "UF" as a locador.
          const c0Letters = (c0.match(/[a-zA-ZñÑáéíóúÁÉÍÓÚ]/g) || []).length;
          if (isPerOwnerFormat && c0 && !isHeaderWord(c0) && c0Letters >= 4) {
            currentLocador = c0;
          }
          return;
        }
        if (isMetadataOnlyDivider) {
          // e.g. DI BAIA: 'CAQUINEAU PATRICIO ... MES INICIO VALOR INICIO IPC ICL'.
          // CAVALLINI lives at col 7 in some sheets, so search the whole row.
          if (isPerOwnerFormat) {
            const found = findLocadorInRow(row);
            if (found) currentLocador = found;
          }
          return;
        }
        // Otherwise it's a payment / cobros / gastos sub-table -> stop
        stopProcessing = true;
        return;
      }

      // Stop when we hit a known non-contract section keyword in col 0
      if (c0 && STOP_KEYWORDS.test(c0)) {
        stopProcessing = true;
        return;
      }

      const locadorRaw = isPerOwnerFormat
        ? currentLocador
        : String(row[cols.locador] || "").trim();
      const locatarioRaw = String(row[cols.locatario] || "").trim();
      const propiedad = cols.propiedad !== undefined ? String(row[cols.propiedad] || "").trim() : "";
      const monto = cols.monto !== undefined ? roundInt(row[cols.monto]) : 0;

      // A real contract requires a tenant and a positive rent.
      // This filters out subtotal rows, vacant-unit placeholders, and stray annotations
      // (e.g. "marcelo" with monto 0, "Belbeze Nº 222" with no tenant).
      if (!locatarioRaw) return;
      if (!monto || monto <= 0) return;

      const locador = parseNombreTelefono(locadorRaw);
      const locatario = parseNombreTelefono(locatarioRaw);

      // Normalize % (could be 0.05 or 5)
      let porcentaje = cols.porcentaje !== undefined ? Number(row[cols.porcentaje]) || 0 : 0;
      if (porcentaje > 0 && porcentaje < 1) porcentaje = porcentaje * 100;
      porcentaje = round2(porcentaje);

      const comisionMonto = cols.adm !== undefined ? roundInt(row[cols.adm]) : 0;
      const totalPropietario = cols.total !== undefined ? roundInt(row[cols.total]) : 0;

      // VENCE - could be serial date or string
      let fechaVencimiento = "";
      const venceRaw = cols.vence !== undefined ? row[cols.vence] : null;
      if (typeof venceRaw === "number") {
        fechaVencimiento = excelDateToISO(venceRaw);
      } else if (typeof venceRaw === "string" && venceRaw.trim()) {
        const dm = venceRaw.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
        if (dm) {
          let y = parseInt(dm[3]); if (y < 100) y += 2000;
          let m = parseInt(dm[2]); let d = parseInt(dm[1]);
          if (m > 12) { const t = d; d = m; m = t; }
          fechaVencimiento = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        }
      }

      // FECHA INICIO
      let fechaInicio = "";
      const inicioRaw = cols.fechaInicio !== undefined ? row[cols.fechaInicio] : null;
      if (typeof inicioRaw === "number") {
        fechaInicio = excelDateToISO(inicioRaw);
      }

      // AUMENTO - parse free-text first, then refine using the dedicated index columns.
      const aumentoRaw = cols.aumento !== undefined ? String(row[cols.aumento] || "") : "";
      const aumento = parseAumento(aumentoRaw);
      aumento.fechaUltimoAumento = fechaInicio;

      // IPC / ICL / CASA PROPIA columns hold the cycle length in months (e.g. 4 = cuatrimestral).
      // Whichever column has a positive number wins; if multiple, IPC > ICL > CASA_PROPIA.
      const readMonths = (key) => {
        if (cols[key] === undefined) return 0;
        const v = row[cols[key]];
        if (typeof v === "number" && v > 0) return v;
        if (typeof v === "string") {
          const m = v.match(/\d+/);
          if (m) return Number(m[0]) || 0;
        }
        return 0;
      };
      const ipcMeses = readMonths("ipc");
      const iclMeses = readMonths("icl");
      const cpMeses = readMonths("casaPropia");
      let frecuenciaMeses = 0;
      if (ipcMeses > 0) { aumento.tipo = "IPC"; frecuenciaMeses = ipcMeses; }
      else if (iclMeses > 0) { aumento.tipo = "ICL"; frecuenciaMeses = iclMeses; }
      else if (cpMeses > 0) { aumento.tipo = "CASA_PROPIA"; frecuenciaMeses = cpMeses; }
      if (frecuenciaMeses > 0) {
        aumento.frecuenciaMeses = frecuenciaMeses;
        aumento.frecuencia =
          frecuenciaMeses === 3 ? "TRIMESTRAL" :
          frecuenciaMeses === 4 ? "CUATRIMESTRAL" :
          frecuenciaMeses === 6 ? "SEMESTRAL" :
          frecuenciaMeses === 12 ? "ANUAL" :
          aumento.frecuencia;
      }

      // VALOR INICIAL
      const valorInicialRaw = cols.valorInicial !== undefined ? row[cols.valorInicial] : null;
      let valorInicial = 0;
      if (typeof valorInicialRaw === "number") {
        valorInicial = valorInicialRaw;
      } else if (typeof valorInicialRaw === "string") {
        const numMatch = valorInicialRaw.match(/(\d[\d.]+)/);
        if (numMatch) valorInicial = Number(numMatch[1]) || 0;
      }
      valorInicial = roundInt(valorInicial);

      // Register locador
      if (locador.nombre) {
        const key = locador.nombre.toLowerCase();
        if (!locadoresMap.has(key)) {
          locadoresMap.set(key, {
            nombre: locador.nombre,
            telefono: locador.telefono,
            telefono2: locador.telefono2,
            email: "",
            observaciones: locador.observaciones,
          });
        }
      }

      // Register locatario
      if (locatario.nombre) {
        const key = locatario.nombre.toLowerCase();
        if (!locatariosMap.has(key)) {
          locatariosMap.set(key, {
            nombre: locatario.nombre,
            telefono: locatario.telefono,
            telefono2: locatario.telefono2,
            email: "",
            dni: "",
            observaciones: locatario.observaciones,
          });
        }
      }

      contratos.push({
        locadorNombre: locador.nombre,
        locatarioNombre: locatario.nombre,
        propiedad,
        montoActual: monto,
        moneda: "ARS",
        comisionPorcentaje: porcentaje,
        comisionMonto,
        totalPropietario,
        fechaInicio,
        fechaVencimiento,
        valorInicial: valorInicial || monto,
        aumento,
        estado: "ACTIVO",
        observaciones: aumentoRaw.trim() ? `Aumento original: ${aumentoRaw.trim()}` : "",
        locadorId: "",
        locatarioId: "",
      });
    });

    setPreview({
      locadores: Array.from(locadoresMap.values()),
      locatarios: Array.from(locatariosMap.values()),
      contratos,
      raw: dataRows.slice(0, 5).map((r) => r.slice(0, 9)),
    });
  };

  const handleSheetChange = (name) => {
    setSelectedSheet(name);
    setResult(null);
    setLocadorOverride("");
    if (workbookRef && xlsxRef) {
      processSheet(workbookRef, xlsxRef, name);
    }
  };

  // Inline-edit helpers for the preview tables. `field` can be a nested path like "aumento.tipo".
  const setNested = (obj, path, value) => {
    const parts = path.split(".");
    const next = { ...obj };
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
      cur[parts[i]] = { ...(cur[parts[i]] || {}) };
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
    return next;
  };
  const updateContrato = (idx, field, value) => {
    setPreview((p) => {
      if (!p) return p;
      const contratos = p.contratos.slice();
      contratos[idx] = setNested(contratos[idx], field, value);
      return { ...p, contratos };
    });
  };
  const updateLocadorRow = (idx, field, value) => {
    setPreview((p) => {
      if (!p) return p;
      const locadores = p.locadores.slice();
      locadores[idx] = { ...locadores[idx], [field]: value };
      return { ...p, locadores };
    });
  };
  const updateLocatarioRow = (idx, field, value) => {
    setPreview((p) => {
      if (!p) return p;
      const locatarios = p.locatarios.slice();
      locatarios[idx] = { ...locatarios[idx], [field]: value };
      return { ...p, locatarios };
    });
  };
  const deleteContrato = (idx) => {
    setPreview((p) => {
      if (!p) return p;
      const contratos = p.contratos.slice();
      contratos.splice(idx, 1);
      return { ...p, contratos };
    });
  };

  const handleImport = async () => {
    if (!preview) return;
    setImporting(true);
    setResult(null);
    try {
      // If the user set an override, every contract gets that locador.
      const override = locadorOverride.trim();
      const effectiveContratos = override
        ? preview.contratos.map((c) => ({ ...c, locadorNombre: override }))
        : preview.contratos;

      // Rebuild locadores/locatarios lists from the (possibly edited) contracts so that
      // renames done in the contratos table flow through. Also pick up any phone/email
      // tweaks the user made in the dedicated panels by matching on nombre.
      const pickedLocadores = new Map();
      const pickedLocatarios = new Map();
      const lookupLocador = (nombre) => preview.locadores.find((l) => l.nombre === nombre);
      const lookupLocatario = (nombre) => preview.locatarios.find((l) => l.nombre === nombre);
      for (const c of effectiveContratos) {
        const lName = (c.locadorNombre || "").trim();
        if (lName && !pickedLocadores.has(lName.toLowerCase())) {
          const existing = lookupLocador(lName);
          pickedLocadores.set(lName.toLowerCase(), existing || { nombre: lName, telefono: "", telefono2: "", email: "", observaciones: "" });
        }
        const tName = (c.locatarioNombre || "").trim();
        if (tName && !pickedLocatarios.has(tName.toLowerCase())) {
          const existing = lookupLocatario(tName);
          pickedLocatarios.set(tName.toLowerCase(), existing || { nombre: tName, telefono: "", telefono2: "", email: "", dni: "", observaciones: "" });
        }
      }
      const effectiveLocadores = Array.from(pickedLocadores.values());
      const effectiveLocatarios = Array.from(pickedLocatarios.values());

      // 1. Create locadores and build name->id map
      const locadorIdMap = {};
      for (const loc of effectiveLocadores) {
        const id = await saveLocador(loc);
        locadorIdMap[loc.nombre.toLowerCase()] = id;
      }

      // 2. Create locatarios
      const locatarioIdMap = {};
      for (const loc of effectiveLocatarios) {
        const id = await saveLocatario(loc);
        locatarioIdMap[loc.nombre.toLowerCase()] = id;
      }

      // 3. Create contratos with linked IDs
      let count = 0;
      for (const contrato of effectiveContratos) {
        await saveContrato({
          ...contrato,
          locadorId: locadorIdMap[contrato.locadorNombre.toLowerCase()] || "",
          locatarioId: locatarioIdMap[contrato.locatarioNombre.toLowerCase()] || "",
        });
        count++;
      }

      await refreshAll();
      setResult({
        success: true,
        message: `Importacion exitosa: ${effectiveLocadores.length} locador${effectiveLocadores.length === 1 ? "" : "es"}, ${effectiveLocatarios.length} locatarios, ${count} contratos.`,
      });
      setPreview(null);
      setSheetNames([]);
      setWorkbookRef(null);
      setLocadorOverride("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (error) {
      console.error("Error al importar:", error);
      setResult({ success: false, message: `Error: ${error.message}` });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className={embedded ? "space-y-6" : "px-4 md:px-6 py-4 space-y-6 max-w-5xl mx-auto"}>
      {!embedded && (
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Importar desde Excel
        </h1>
      )}
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Subi el Excel de alquileres. Se crean automaticamente locadores, locatarios y contratos.
      </p>

      {/* Upload */}
      <div
        onClick={() => fileRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
          isDragging
            ? "border-primary bg-primary/5 dark:bg-primary/10"
            : "border-gray-300 dark:border-gray-600 hover:border-primary"
        }`}
      >
        <FaFileUpload
          className={`w-10 h-10 mx-auto mb-3 transition-colors ${
            isDragging ? "text-primary" : "text-gray-400"
          }`}
        />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {isDragging
            ? "Soltá el archivo acá"
            : "Click o arrastrá el archivo Excel (.xlsx) acá"}
        </p>
        {fileName && !isDragging && (
          <p className="text-xs text-gray-400 mt-2">Último: {fileName}</p>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFile}
          className="hidden"
        />
      </div>

      {/* Sheet selector */}
      {sheetNames.length > 1 && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Hoja:</span>
          <div className="flex gap-2">
            {sheetNames.map((name) => (
              <button
                key={name}
                onClick={() => handleSheetChange(name)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  selectedSheet === name
                    ? "bg-primary text-white"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Result message */}
      {result && (
        <div
          className={`rounded-lg p-4 flex items-start gap-3 ${
            result.success
              ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
              : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
          }`}
        >
          {result.success ? <FaCheck className="mt-0.5" /> : <FaTimes className="mt-0.5" />}
          <p className="text-sm">{result.message}</p>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="space-y-4">
          {/* Locador override */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 space-y-2">
            <label className="text-sm font-medium text-amber-900 dark:text-amber-200 block">
              Override de locador (opcional)
            </label>
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Si lo completas, se aplica a TODOS los contratos de esta hoja sobreescribiendo los detectados automaticamente.
              Usar cuando el archivo tiene un solo locador y la deteccion automatica agarro un valor incorrecto (ej. NINA, DOLORES/TATA).
            </p>
            <input
              type="text"
              value={locadorOverride}
              onChange={(e) => setLocadorOverride(e.target.value)}
              placeholder={preview.locadores.length === 1 ? `Detectado: ${preview.locadores[0].nombre}` : "Dejar vacio para usar los detectados"}
              className="w-full px-3 py-2 text-sm rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 text-center">
              <p className="text-2xl font-bold text-primary">{locadorOverride.trim() ? 1 : preview.locadores.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Locador{(locadorOverride.trim() ? 1 : preview.locadores.length) === 1 ? "" : "es"}
                {locadorOverride.trim() && <span className="block text-xs text-amber-600 dark:text-amber-400">(override)</span>}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 text-center">
              <p className="text-2xl font-bold text-emerald-500">{preview.locatarios.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Locatarios</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 text-center">
              <p className="text-2xl font-bold text-violet-500">{preview.contratos.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Contratos</p>
            </div>
          </div>

          {/* Contratos preview */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                Vista previa de contratos (primeros 20)
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <th className="text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Locador</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Locatario</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Propiedad</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Monto</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">%</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Comision</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Total Prop.</th>
                    <th className="text-center px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Vence</th>
                    <th className="text-center px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Inicio</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Valor inicial</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Indice</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Meses</th>
                    <th className="text-center px-2 py-2 font-semibold text-gray-600 dark:text-gray-400">Prox. aum.</th>
                    <th className="text-center px-2 py-2 font-semibold text-gray-600 dark:text-gray-400"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {preview.contratos.map((c, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-2 py-1 whitespace-nowrap">
                        {locadorOverride.trim() ? (
                          <span className="text-amber-700 dark:text-amber-300 font-medium px-2 py-1">{locadorOverride.trim()}</span>
                        ) : (
                          <input
                            type="text"
                            value={c.locadorNombre || ""}
                            onChange={(e) => updateContrato(i, "locadorNombre", e.target.value)}
                            className={CELL_INPUT_CLS}
                          />
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          value={c.locatarioNombre || ""}
                          onChange={(e) => updateContrato(i, "locatarioNombre", e.target.value)}
                          className={CELL_INPUT_CLS}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          value={c.propiedad || ""}
                          onChange={(e) => updateContrato(i, "propiedad", e.target.value)}
                          className={CELL_INPUT_CLS}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          value={c.montoActual || 0}
                          onChange={(e) => updateContrato(i, "montoActual", Number(e.target.value) || 0)}
                          className={`${CELL_INPUT_CLS} text-right font-medium`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          step="0.01"
                          value={c.comisionPorcentaje || 0}
                          onChange={(e) => updateContrato(i, "comisionPorcentaje", Number(e.target.value) || 0)}
                          className={`${CELL_INPUT_CLS} text-right w-14`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          value={c.comisionMonto || 0}
                          onChange={(e) => updateContrato(i, "comisionMonto", Number(e.target.value) || 0)}
                          className={`${CELL_INPUT_CLS} text-right`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          value={c.totalPropietario || 0}
                          onChange={(e) => updateContrato(i, "totalPropietario", Number(e.target.value) || 0)}
                          className={`${CELL_INPUT_CLS} text-right text-green-700 dark:text-green-400 font-medium`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="date"
                          value={c.fechaVencimiento || ""}
                          onChange={(e) => updateContrato(i, "fechaVencimiento", e.target.value)}
                          className={`${CELL_INPUT_CLS} text-center`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="date"
                          value={c.fechaInicio || ""}
                          onChange={(e) => updateContrato(i, "fechaInicio", e.target.value)}
                          className={`${CELL_INPUT_CLS} text-center`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          value={c.valorInicial || 0}
                          onChange={(e) => updateContrato(i, "valorInicial", Number(e.target.value) || 0)}
                          className={`${CELL_INPUT_CLS} text-right`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <select
                          value={c.aumento?.tipo || ""}
                          onChange={(e) => updateContrato(i, "aumento.tipo", e.target.value)}
                          className={CELL_INPUT_CLS}
                        >
                          <option value="">-</option>
                          <option value="IPC">IPC</option>
                          <option value="ICL">ICL</option>
                          <option value="CASA_PROPIA">CASA_PROPIA</option>
                          <option value="INDEX">INDEX</option>
                          <option value="FIJO">FIJO</option>
                        </select>
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          value={c.aumento?.frecuenciaMeses || 0}
                          onChange={(e) => updateContrato(i, "aumento.frecuenciaMeses", Number(e.target.value) || 0)}
                          className={`${CELL_INPUT_CLS} text-right w-14`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="date"
                          value={c.aumento?.proximoAumento || ""}
                          onChange={(e) => updateContrato(i, "aumento.proximoAumento", e.target.value)}
                          className={`${CELL_INPUT_CLS} text-center`}
                        />
                      </td>
                      <td className="px-2 py-1 text-center">
                        <button
                          type="button"
                          onClick={() => deleteContrato(i)}
                          title="Eliminar fila"
                          className="w-6 h-6 inline-flex items-center justify-center rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 text-center py-2">
              {preview.contratos.length} contratos — tocá cualquier celda para editar
            </p>
          </div>

          {/* Locadores preview */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {locadorOverride.trim()
                  ? `Locador (override) — los ${preview.locadores.length} detectados se ignoran`
                  : `Locadores detectados (${preview.locadores.length})`}
              </h3>
            </div>
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0">
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Nombre</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Telefono</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Telefono 2</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Observaciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {locadorOverride.trim() ? (
                    <tr>
                      <td className="px-3 py-1.5 text-amber-700 dark:text-amber-300 font-medium">{locadorOverride.trim()}</td>
                      <td className="px-3 py-1.5 text-gray-500">-</td>
                      <td className="px-3 py-1.5 text-gray-500">-</td>
                      <td className="px-3 py-1.5 text-gray-500">-</td>
                    </tr>
                  ) : (
                    preview.locadores.map((l, i) => (
                      <tr key={i}>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            value={l.nombre || ""}
                            onChange={(e) => updateLocadorRow(i, "nombre", e.target.value)}
                            className={CELL_INPUT_CLS}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            value={l.telefono || ""}
                            onChange={(e) => updateLocadorRow(i, "telefono", e.target.value)}
                            className={CELL_INPUT_CLS}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            value={l.telefono2 || ""}
                            onChange={(e) => updateLocadorRow(i, "telefono2", e.target.value)}
                            className={CELL_INPUT_CLS}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            value={l.observaciones || ""}
                            onChange={(e) => updateLocadorRow(i, "observaciones", e.target.value)}
                            className={CELL_INPUT_CLS}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Locatarios preview */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                Locatarios detectados ({preview.locatarios.length})
              </h3>
            </div>
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0">
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Nombre</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Telefono</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Telefono 2</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400">Observaciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {preview.locatarios.map((l, i) => (
                    <tr key={i}>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          value={l.nombre || ""}
                          onChange={(e) => updateLocatarioRow(i, "nombre", e.target.value)}
                          className={CELL_INPUT_CLS}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          value={l.telefono || ""}
                          onChange={(e) => updateLocatarioRow(i, "telefono", e.target.value)}
                          className={CELL_INPUT_CLS}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          value={l.telefono2 || ""}
                          onChange={(e) => updateLocatarioRow(i, "telefono2", e.target.value)}
                          className={CELL_INPUT_CLS}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          value={l.observaciones || ""}
                          onChange={(e) => updateLocatarioRow(i, "observaciones", e.target.value)}
                          className={CELL_INPUT_CLS}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Import button */}
          <div className="flex justify-end">
            <button
              onClick={handleImport}
              disabled={importing}
              className="inline-flex items-center gap-2 px-6 py-3 text-sm bg-primary hover:bg-secondary text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {importing ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" /> Importando...
                </>
              ) : (
                <>
                  <FaCheck className="w-4 h-4" /> Importar todo ({locadorOverride.trim() ? 1 : preview.locadores.length} locador{(locadorOverride.trim() ? 1 : preview.locadores.length) === 1 ? "" : "es"} + {preview.locatarios.length} locatarios + {preview.contratos.length} contratos)
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImportContratos;
